import React, { useState } from 'react';
import { contentAPI } from '../../services/api';
import {
  GALLERY_CATEGORY_OPTIONS,
  getGalleryCategory,
  getGalleryPreviewUrl,
  getGalleryVideoEmbedUrl,
} from '../../utils/gallery';
import './GalleryAdminPanel.css';

const BLANK_FORM = {
  title: '', image_url: '', media_type: 'image', thumbnail_url: '', alt_text: '', caption: '',
  category: 'Community', event_date: '', sort_order: 0, is_featured: false, is_published: true,
};

const formFromItem = item => ({
  ...BLANK_FORM,
  ...item,
  thumbnail_url: item.thumbnail_url || '',
  alt_text: item.alt_text || '',
  caption: item.caption || '',
  event_date: item.event_date || '',
  sort_order: Number(item.sort_order || 0),
  is_featured: item.is_featured === true,
  is_published: item.is_published !== false,
});

export default function GalleryAdminPanel({ slug, items, onReload, onMessage }) {
  const [form, setForm] = useState(BLANK_FORM);
  const [editingId, setEditingId] = useState(null);
  const [viewingItem, setViewingItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const setField = (field, value) => setForm(current => ({ ...current, [field]: value }));
  const closeForm = () => { setForm(BLANK_FORM); setEditingId(null); };
  const startNewItem = () => {
    closeForm();
    window.requestAnimationFrame(() => document.querySelector('.gallery-admin-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  };

  const editItem = item => {
    setForm(formFromItem(item));
    setEditingId(item.id);
    window.requestAnimationFrame(() => document.querySelector('.gallery-admin-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  };

  const saveItem = async event => {
    event.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, sort_order: Number(form.sort_order || 0) };
      if (editingId) await contentAPI.updateGalleryMedia(slug, editingId, payload);
      else await contentAPI.addGalleryImage(slug, payload);
      onMessage(editingId ? 'गॅलरी मीडिया अद्ययावत केले.' : 'गॅलरी मीडिया जोडले.');
      closeForm();
      await onReload();
    } catch (error) {
      onMessage(error.response?.data?.error || 'गॅलरी मीडिया जतन करण्यात अयशस्वी.', true);
    } finally {
      setSaving(false);
    }
  };

  const uploadMedia = async event => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      const body = new FormData();
      body.append('media', file);
      const response = await contentAPI.uploadGalleryMedia(slug, body);
      setForm(current => ({
        ...current,
        image_url: response.data.url,
        media_type: response.data.media_type,
        title: current.title || file.name.replace(/\.[^.]+$/, '').replace(/[-_]+/g, ' '),
      }));
      onMessage('फाइल अपलोड झाली. तपशील भरून जतन करा.');
    } catch (error) {
      onMessage(error.response?.data?.error || 'फाइल अपलोड करण्यात अयशस्वी.', true);
    } finally {
      setUploading(false);
    }
  };

  const deleteItem = async item => {
    if (!window.confirm(`“${item.title || 'Untitled'}” गॅलरीमधून कायमचे काढायचे?`)) return;
    try {
      await contentAPI.deleteGalleryImage(slug, item.id);
      onMessage('गॅलरी मीडिया काढले.');
      await onReload();
    } catch {
      onMessage('गॅलरी मीडिया काढण्यात अयशस्वी.', true);
    }
  };

  return (
    <section className="gallery-admin-panel">
      <div className="gallery-admin-head">
        <div>
          <span>सार्वजनिक संकेतस्थळ</span>
          <h3>फोटो आणि व्हिडिओ गॅलरी</h3>
          <p>वर्ग, दिनांक, क्रम आणि प्रकाशन स्थितीसह मीडिया व्यवस्थापित करा.</p>
        </div>
        <button className="btn btn-primary btn-sm" type="button" onClick={startNewItem}>+ नवीन मीडिया</button>
      </div>

      <form className="gallery-admin-form" onSubmit={saveItem}>
        <div className="gallery-admin-form-title">
          <div><strong>{editingId ? 'मीडिया संपादित करा' : 'नवीन मीडिया जोडा'}</strong><small>URL द्या किंवा स्थानिक फाइल अपलोड करा.</small></div>
          <label className={`gallery-upload-button${uploading ? ' disabled' : ''}`}>
            {uploading ? 'अपलोड होत आहे…' : 'फाइल अपलोड'}
            <input type="file" accept="image/jpeg,image/png,image/gif,image/webp,video/mp4,video/webm,video/ogg" onChange={uploadMedia} disabled={uploading} />
          </label>
        </div>

        <div className="gallery-admin-fields">
          <label className="wide">मीडिया URL *<input required value={form.image_url} onChange={event => setField('image_url', event.target.value)} placeholder="https://… किंवा /uploads/…" /></label>
          <label>प्रकार *<select value={form.media_type} onChange={event => setField('media_type', event.target.value)}><option value="image">फोटो</option><option value="video">व्हिडिओ</option></select></label>
          <label>वर्ग *<select value={form.category} onChange={event => setField('category', event.target.value)}>{GALLERY_CATEGORY_OPTIONS.map(category => <option key={category.value} value={category.value}>{category.label}</option>)}</select></label>
          <label className="wide">शीर्षक *<input required maxLength="200" value={form.title} onChange={event => setField('title', event.target.value)} /></label>
          <label>कार्यक्रमाचा दिनांक<input type="date" value={form.event_date} onChange={event => setField('event_date', event.target.value)} /></label>
          <label>दाखवण्याचा क्रम<input type="number" min="0" max="9999" value={form.sort_order} onChange={event => setField('sort_order', event.target.value)} /></label>
          <label className="wide">थंबनेल URL <small>(व्हिडिओसाठी उपयुक्त)</small><input value={form.thumbnail_url} onChange={event => setField('thumbnail_url', event.target.value)} placeholder="YouTube व्हिडिओसाठी आपोआप तयार होते" /></label>
          <label className="wide">पर्यायी मजकूर<input maxLength="200" value={form.alt_text} onChange={event => setField('alt_text', event.target.value)} placeholder="दृष्टीदोष असलेल्या वापरकर्त्यांसाठी दृश्याचे वर्णन" /></label>
          <label className="wide">कॅप्शन<textarea rows="3" maxLength="500" value={form.caption} onChange={event => setField('caption', event.target.value)} /></label>
        </div>

        <div className="gallery-admin-flags">
          <label><input type="checkbox" checked={form.is_featured} onChange={event => setField('is_featured', event.target.checked)} /> वैशिष्ट्यीकृत</label>
          <label><input type="checkbox" checked={form.is_published} onChange={event => setField('is_published', event.target.checked)} /> सार्वजनिकरीत्या प्रकाशित</label>
        </div>
        <div className="gallery-admin-actions">
          <button className="btn btn-primary btn-sm" type="submit" disabled={saving || uploading}>{saving ? 'जतन होत आहे…' : editingId ? 'बदल जतन करा' : 'मीडिया जतन करा'}</button>
          {editingId && <button className="btn btn-outline btn-sm" type="button" onClick={closeForm}>रद्द करा</button>}
        </div>
      </form>

      {items.length === 0 ? (
        <div className="gallery-admin-empty"><strong>गॅलरी रिकामी आहे</strong><span>वरील फॉर्ममधून पहिला फोटो किंवा व्हिडिओ जोडा.</span></div>
      ) : (
        <div className="gallery-admin-grid">
          {items.map(item => {
            const preview = getGalleryPreviewUrl(item);
            return (
              <article className={`gallery-admin-card${item.is_published === false ? ' unpublished' : ''}`} key={item.id}>
                <div className="gallery-admin-preview">
                  {preview ? <img src={preview} alt="" /> : item.media_type === 'video' ? <span className="gallery-admin-video-placeholder">▶</span> : <span>प्रतिमा उपलब्ध नाही</span>}
                  <b>{item.media_type === 'video' ? 'व्हिडिओ' : 'फोटो'}</b>
                  {item.is_featured && <em>वैशिष्ट्यीकृत</em>}
                </div>
                <div className="gallery-admin-card-copy">
                  <span>{getGalleryCategory(item.category).label}{item.event_date ? ` · ${item.event_date}` : ''}</span>
                  <h4>{item.title || 'शीर्षक नाही'}</h4>
                  <p>{item.caption || 'कॅप्शन नाही'}</p>
                  <small>{item.is_published === false ? 'अप्रकाशित' : 'प्रकाशित'} · क्रम {item.sort_order || 0}</small>
                </div>
                <div className="gallery-admin-card-actions">
                  <button type="button" onClick={() => setViewingItem(item)}>पहा</button>
                  <button type="button" onClick={() => editItem(item)}>संपादित करा</button>
                  <button className="danger" type="button" onClick={() => deleteItem(item)}>काढा</button>
                </div>
              </article>
            );
          })}
        </div>
      )}

      {viewingItem && (
        <div className="gallery-admin-modal" role="presentation" onClick={() => setViewingItem(null)}>
          <div role="dialog" aria-modal="true" aria-label={viewingItem.title} onClick={event => event.stopPropagation()}>
            <button className="gallery-admin-modal-close" type="button" onClick={() => setViewingItem(null)}>×</button>
            {viewingItem.media_type === 'video' && getGalleryVideoEmbedUrl(viewingItem)
              ? <iframe src={getGalleryVideoEmbedUrl(viewingItem)} title={viewingItem.title} allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen />
              : viewingItem.media_type === 'video'
              ? <video src={viewingItem.image_url} controls />
              : <img src={viewingItem.image_url} alt={viewingItem.alt_text || viewingItem.title} />}
            <h3>{viewingItem.title}</h3>
            {viewingItem.caption && <p>{viewingItem.caption}</p>}
          </div>
        </div>
      )}
    </section>
  );
}
